import type { IntlShape } from 'react-intl';
import type { SupportedMimeType } from '../../../contracts';
import {
  MAX_FILE_SIZE_MB,
  SUPPORTED_FILE_EXTENSION_TO_MIME,
  SUPPORTED_FILE_TYPES,
} from '../../../contracts';
import { uploadValidationMessages } from '../uploadValidation.messages';

export interface FileValidationResult {
  valid: boolean;
  error: string | null;
}

export interface FileValidationMessages {
  unsupportedType: string;
  tooLarge: string;
}

/**
 * Validates an upload candidate against the shared supported-type allowlist.
 * Rejects legacy `.doc` explicitly so the caller can surface an actionable
 * re-save message (BE does not accept them — see DEV-11423).
 */
export const validateUploadFile = (
  file: File,
  intl: IntlShape,
  messages: FileValidationMessages
): FileValidationResult => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (file.type === 'application/msword' || extension === 'doc') {
    return {
      valid: false,
      error: intl.formatMessage(uploadValidationMessages.legacyDocUnsupported),
    };
  }

  const mimeType = file.type as SupportedMimeType;
  let fileConfig = SUPPORTED_FILE_TYPES[mimeType];

  // Some browsers report application/octet-stream or empty for recognized extensions.
  if (!fileConfig) {
    const mappedMime = extension
      ? SUPPORTED_FILE_EXTENSION_TO_MIME[extension]
      : undefined;
    if (mappedMime) {
      fileConfig = SUPPORTED_FILE_TYPES[mappedMime];
    }
  }

  if (!fileConfig) {
    return { valid: false, error: messages.unsupportedType };
  }
  if (file.size > fileConfig.maxSize) {
    return { valid: false, error: messages.tooLarge };
  }
  return { valid: true, error: null };
};

export { MAX_FILE_SIZE_MB };
